/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isNull from 'lodash.isnull';
import {APIGatewaySmartSearch} from '@ion/core/src/types/services';
import {convertModularSearchDateRange} from '@ion/core/src/utils/DateUtil';
import {LMUCategories, PLATFORM_IDS} from '@ion/core/src/utils/DeviceUtils';
import {SearchFilters, SearchSentinelTagType} from '@ion/features/src/search/types';
import {
  MAX_IDENTIFIER_SEARCH_LIMIT,
  NO_ID_REPORT
} from '@ion/features/src/search/utils';
import {
  getComboFields,
  getDefaultSortParam,
  getSortOn,
  hasDefaultExtensionsSortParam
} from '../../helpers';
import {
  DEFAULT_SMART_SEARCH_QUERY,
  SearchFields,
  SentinelTags
} from './types';

const identifiers  = [
  'any',
  'esn',
  'lmu',
  'mdt',
  'appId',
  'jpodConfig',
  'androidMdtFirmware',
  'sentinelAssociation',
  'ble3Firmware',
  'jpodFirmware',
  'vbusDatabase',
  'serialNumber',
  'vbu3Firmware',
  'radioFirmware'
];

const nestedFields = [
  'JPOD Firmware',
  'VBUS Database',
  'JPOD Config',
  'Android MDT Firmware',
  'BLE3 Firmware',
  'VBU3 Firmware',
  'Radio Firmware'
];

export enum DefaultSortParam {
  ID = 'id',
  JPOD_FIRMWARE = 'JPOD Firmware',
  VBUS_DATABASE = 'VBUS Database',
  JPOD_CONFIG = 'JPOD Config',
  ANDROID_MDT_FIRMWARE = 'Android MDT Firmware',
  BLE3_FIRWARE = 'BLE3 Firmware',
  VBU3_FIRWARE = 'VBU3 Firmware',
  RADIO_FIRMWARE = 'Radio Firmware',
  APP_ID = 'extensions.appId',
  FIRMWARE_VERSION = 'extensions.firmwareVersion',
  CONFIGURATION_VERSION = 'extensions.configVersion',
  FIRMWARE_JPOD = '-JPOD Firmware',
  DATABASE_VBUS = '-VBUS Database',
  CONFIG_JPOD = '-JPOD Config',
  FIRMWARE_ANDROID_MDT = '-Android MDT Firmware',
  FIRWARE_BLE3 = '-BLE3 Firmware',
  FIRWARE_VBU3 = '-VBU3 Firmware',
  FIRMWARE_RADIO = '-Radio Firmware',
  FILE_VERSION = 'extensions.fileVersions.version',
  ID_REPORT_TIMESTAMP = 'extensions.idReportTimestamp'
};

/**
   * Given search filters will create a device specific query string to utilize "filter by smart search".
   */
export const createModularSearchQueryString = (search: SearchFilters = {}): string => {
  if (!isNil(search)) {
    const result = Object.entries(search)
      .filter(([, value]) => !isNull(value))
      .map(([key, value]) => {
        switch (key) {
          case SearchFields.ANY:
            return `(${replaceQuery(value, ', ', ' OR ')})`;
          case SearchFields.GROUPS:
            return `${key}: (${value === -1 ? '!*' : value})`;
          case SearchFields.APP_ID:
          case SearchFields.FIRMWARE_VERSION:
          case SearchFields.ANDROID_OS_VERSION:
          case SearchFields.CONFIG_VERSION:
          case SearchFields.VBUS_STATUS:
          case SearchFields.VBUS_MODE:
            return `extensions.${key}: (${value})`;
          case SearchFields.SCRIPT_VERSION:
            return `extensions.${key}: (${value})`;
          case SearchFields.MDT:
          case SearchFields.LMU:
            return `extensions.${key}.mobileId: (${value})`;
          case SearchFields.SENTINEL_ASSOCIATION:
            return `${key}: (${replaceQuery(value, ', ', ' OR ')})`;
          case SearchFields.ID_REPORT_TIMESTAMP:
            if (value === NO_ID_REPORT) return;
            return `extensions.${key}: (${convertModularSearchDateRange(value, 'YYYY-MM-DD[T]HH[\\]:mm[\\]:ss.[000Z]')})`;
          case SearchFields.LAST_REPORT_DATE:
            return `${key}: (${convertModularSearchDateRange(value, 'YYYY-MM-DD[T]HH[\\]:mm[\\]:ss.[000Z]')})`;
          case SearchFields.STATUS:
            return `${key}: (${replaceQuery(value, ', ', ' OR ')})`;
          default:
            return `${key}: (${replaceQuery(value, ', ', ' OR ')})`;
        }
      });
    return result.length ? `(${result.join(') AND (')})` : DEFAULT_SMART_SEARCH_QUERY;
  }
  return DEFAULT_SMART_SEARCH_QUERY;
};

// defines the common extension search query parameters ...
const searchExtensions = (key, value) => (
  {wildcard: {[`extensions.${key}`]: value}}
);

const getAssociatedLMUQuery = (key) => {
  if (isEqual(key, SearchFields.LMU)) {
    return {
      bool : {
        should : [
          {
            wildcard : {'extensions.associatedDevices.deviceCategory': key?.toUpperCase()}
          },
          {
            wildcard : {'extensions.associatedDevices.deviceCategory': LMUCategories.LMU_TTU}
          }
        ]
      }
    };
  }
  return {wildcard: {'extensions.associatedDevices.deviceCategory': key?.toUpperCase()}};
};

const getAssociatedDeviceQuery = (key, value) => ({
  nestedSearch : {
    nested : {
      path  : 'extensions.associatedDevices',
      query : {
        bool : {
          must : [
            {wildcard: {'extensions.associatedDevices.mobileId': value}},
            getAssociatedLMUQuery(key)
          ]
        }
      }
    }
  }
});

/**
 * This function returns the Sentinel tags value based on the
 * selected sentinel tag type.
 */
const getSentinelTagsValue = (tagType: SearchSentinelTagType) => {
  const tagsMap = {
    [SearchSentinelTagType.PRIMARY]   : [SentinelTags.SECONDARY],
    [SearchSentinelTagType.SECONDARY] : [SentinelTags.PRIMARY],
    [SearchSentinelTagType.ALL]       : [SentinelTags.PRIMARY, SentinelTags.SECONDARY]
  };
  return tagsMap[tagType] || [];
};

const getIdReportTimeStamp = () => ({
  not_match : {
    exists : {
      field : 'extensions.idReportTimestamp'
    }
  }
});

const getSentinelDeviceQuery = (key, value) => ({
  nestedSearch : {
    nested : {
      path  : 'extensions.associatedDevices',
      query : {
        terms : {
          'extensions.associatedDevices.tags' :
            getSentinelTagsValue(value)
        }
      }
    }
  }
});

// defines mappings of jpod firmware platform ids.
export const firmwarePlatformIds = [PLATFORM_IDS.jpodFirmware, PLATFORM_IDS.jpod2Firmware];

export const vbuFirmwarePlatformIds = [
  PLATFORM_IDS.jpodFirmware,
  PLATFORM_IDS.jpod2Firmware,
  PLATFORM_IDS.obd1Type1Firmware,
  PLATFORM_IDS.obd2Type2Firmware,
  PLATFORM_IDS.vbu3Firmware
];

// defines mappings of jpod config platform ids.
export const configPlatformIds = [PLATFORM_IDS.jpodConfig, PLATFORM_IDS.jpod2Config];

/**
 * This helper function checks in case if filter fields are one of Jpod Firmware
 * or Jpod Config fields, then provide match payload for smart search.
 */
const getMatchQuery = (ids) => {
  return ids.map(id => ({
    match : {
      'extensions.fileVersions.platform' : id
    }
  }));
};

const getVBUSDatabaseQuery = (value) => ({
  nestedSearch : {
    nested : {
      path  : 'extensions.fileVersions',
      query : {
        bool : {
          must : [
            {
              match : {
                'extensions.fileVersions.version' : value
              }
            },
            {
              bool : {
                should : getMatchQuery([PLATFORM_IDS.obd2Type2DatabaseObd2])
              }
            }
          ]
        }
      }
    }
  }
});

const getRadioFirmwareQuery = (value) => ({
  nestedSearch : {
    nested : {
      path  : 'extensions.fileVersions',
      query : {
        bool : {
          must : [
            {
              wildcard : {
                'extensions.fileVersions.version' : {
                  value : `*${value}*`
                }
              }
            },
            {
              bool : {
                should : getMatchQuery([PLATFORM_IDS.bspModemFirmware, PLATFORM_IDS.cellRadioFirmware])
              }
            }
          ]
        }
      }
    }
  }
});

const getJPODFirmwareConfigQuery = (key, value, isVbuFwEnabled = true) => ({
  nestedSearch : {
    nested : {
      path  : 'extensions.fileVersions',
      query : {
        bool : {
          must : [
            {
              match : {
                'extensions.fileVersions.version' : value
              }
            },
            {
              bool : {
                should : getMatchQuery(key === 'jpodFirmware' ? isVbuFwEnabled ? firmwarePlatformIds : vbuFirmwarePlatformIds : configPlatformIds)
              }
            }
          ]
        }
      }
    }
  }
});

const getFilterQuery = (key, value) => ({
  nestedSearch : {
    nested : {
      path  : 'extensions.fileVersions',
      query : {
        bool : {
          must : [
            {
              wildcard : {
                'extensions.fileVersions.version' : {
                  value : `*${value}*`
                }
              }
            },
            {
              bool : {
                should : getMatchQuery([PLATFORM_IDS[key]])
              }
            }
          ]
        }
      }
    }
  }
});

// defines mappings of search fields to search query parameters.
const searchQueries = {
  [SearchFields.ID] : (key, value) => (
    {match: {[key]: replaceQuery(value, ' OR ', ', ')}}
  ),
  [SearchFields.ANY] : (key, value) => (
    {
      query_string : {
        query  : replaceQuery(value, ' OR ', ', ') || value,
        fields : getComboFields()
      }
    }
  ),
  [SearchFields.GROUPS] : (key, value) => (
    value === -1 ? {not_match: {wildcard: {[key]: '?*'}}} : {wildcard: {[key]: value}}
  ),
  [SearchFields.ID_REPORT_TIMESTAMP] : (key, value) => {
    if (value === NO_ID_REPORT) {
      return getIdReportTimeStamp();
    }
    const dates = convertModularSearchDateRange(value, 'YYYY-MM-DD[T]HH[\]:mm[\]:ss.[000Z]', null);
    return (
      {
        range : {
          [`extensions.${key}`] : {
            gte : dates.from,
            lte : dates.to
          }
        }
      }
    );
  },
  [SearchFields.LAST_REPORT_DATE] : (key, value) => {
    const dates = convertModularSearchDateRange(value, 'YYYY-MM-DD[T]HH[\]:mm[\]:ss.[000Z]', null);
    return (
      {
        range : {
          [key] : {
            gte : dates.from,
            lte : dates.to
          }
        }
      }
    );
  },
  [SearchFields.STATUS] : (key, value) => (
    {wildcard: {[key]: replaceQuery(value, ', ', ' OR ')}}
  ),
  [SearchFields.SENTINEL_ASSOCIATION] : getSentinelDeviceQuery,
  [SearchFields.MDT]                  : getAssociatedDeviceQuery,
  [SearchFields.LMU]                  : getAssociatedDeviceQuery,
  [SearchFields.APP_ID]               : searchExtensions,
  [SearchFields.FIRMWARE_VERSION]     : searchExtensions,
  [SearchFields.ANDROID_OS_VERSION]   : searchExtensions,
  [SearchFields.CONFIG_VERSION]       : searchExtensions,
  [SearchFields.SCRIPT_VERSION]       : searchExtensions,
  [SearchFields.VBUS_STATUS]          : searchExtensions,
  [SearchFields.VBUS_MODE]            : searchExtensions,
  [SearchFields.JPOD_CONFIG]          : getJPODFirmwareConfigQuery,
  [SearchFields.JPOD_FIRMWARE]        : getJPODFirmwareConfigQuery,
  [SearchFields.ANDROID_MDT_FIRMWARE] : getFilterQuery,
  [SearchFields.VBUS_DATABASE]        : getVBUSDatabaseQuery,
  [SearchFields.BLE3_FIRWARE]         : getFilterQuery,
  [SearchFields.VBU3_FIRWARE]         : getFilterQuery,
  [SearchFields.RADIO_FIRMWARE]       : getRadioFirmwareQuery
};

/**
 * Given a search field (key) and value, returns the given search query parameters
 * or fallback search query parameters if one has not been implemented.
*/
const searchQueryParameters = (key, value, isVbuFwEnabled = true) => (
  searchQueries[key]?.(key, value, isVbuFwEnabled) || {wildcard: {[key]: replaceQuery(value, ' OR ', ', ') || value}}
);

const isExtendedField = (key: string): boolean => {
  const fields = ['appId', 'androidOSVersion', 'firmwareVersion', 'vbusEnabledStatus', 'idReportTimestamp', 'configVersion', 'vbusMode'];
  return Boolean(fields.find(field => isEqual(field, key)));
};

const identifierSearchQueryParameters = (key, value) => ({
  query_string : {
    query  : isEqual('lastReportedTime', key) || isEqual('idReportTimestamp', key) ? convertModularSearchDateRange(value, 'YYYY-MM-DD[T]HH[\\]:mm[\\]:ss.[000Z]') : value,
    fields : isEqual('any', key) ? getComboFields() : [isExtendedField(key) ? `extensions.${key}` : key]
  }
});

const replaceQuery = (searchVal, replacedBy: string, replaceWith: string = '') => {
  if (typeof searchVal === 'string') {
    return searchVal?.replace(replacedBy, replaceWith) || searchVal;
  } else {
    return searchVal;
  }
};

const hasIdentifierType = (identifierType) => {
  return identifiers.every(key => !isNil(identifierType?.[key]));
};

const identifierType = (identifier: SearchFilters = {}) => {
  if (!hasIdentifierType(identifier)) {
    const {
      any,
      esn,
      lmu,
      mdt,
      appId,
      jpodConfig,
      androidMdtFirmware,
      sentinelAssociation,
      ble3Firmware,
      jpodFirmware,
      vbusDatabase,
      vbu3Firmware,
      serialNumber,
      radioFirmware
    } = identifier || {};

    const associatedIdentifiers = lmu || mdt || sentinelAssociation;
    const jpodIdentifiers       = jpodConfig || jpodFirmware;
    const vbusIdentifiers       = vbusDatabase;
    const firmwareIdentifiers   = androidMdtFirmware || ble3Firmware ||
    vbu3Firmware || radioFirmware;
    const identifiers           = esn || serialNumber || appId || any;
    const identifierValue       = identifiers?.split(' OR ');

    const hasIdentifiers = [
      identifierValue?.length,
      jpodIdentifiers,
      vbusIdentifiers,
      firmwareIdentifiers,
      associatedIdentifiers
    ];

    return hasIdentifiers.some(Boolean);
  }
};

/**
 * This helper function checks in case if filter fields are one of Associated LMU
 * Or Associated MDT fields, then provide nested payload for smart search
 * Which support Any field with other fields.
 */
const getIdentifiers = ([key, value], isVbuFwEnabled = true) => {
  if (key === 'lmu' || key === 'mdt') {
    return getAssociatedDeviceQuery(key, value)?.nestedSearch;
  } else if (key === 'sentinelAssociation') {
    return getSentinelDeviceQuery(key, value)?.nestedSearch;
  } else if (key === 'jpodFirmware' || key === 'jpodConfig') {
    return getJPODFirmwareConfigQuery(key, value, isVbuFwEnabled)?.nestedSearch;
  } else if (key === 'vbusDatabase') {
    return getVBUSDatabaseQuery(value)?.nestedSearch;
  } else if (key === 'androidMdtFirmware' || key === 'ble3Firmware' || key === 'vbu3Firmware') {
    return getFilterQuery(key, value)?.nestedSearch;
  } else if (key === 'radioFirmware') {
    return getRadioFirmwareQuery(value)?.nestedSearch;
  } else if ((key === 'esn' || key === 'serialNumber') && value?.split(' OR ')?.length > MAX_IDENTIFIER_SEARCH_LIMIT) {
    return searchTerms(key, value);
  } else if (value === NO_ID_REPORT) {
    return getIdReportTimeStamp();
  } else {
    return identifierSearchQueryParameters(key, value);
  }
};

/**
 * Filters will create a device smart serach query along with sorting specific fields.
 */
export const createSortAndSearchQueryParameters = (search: SearchFilters = {}, isVbuFwEnabled = true) => {
  if (!isNil(search)) {
    const items = Object.entries(search).filter(
      ([, value]) => !isNil(value)
    );
    const identifierLength = identifierType(search);
    if (identifierLength) {
      return items.map(item => getIdentifiers(item, isVbuFwEnabled)) || [];
    } else {
      return items.map(([key, value]) => searchQueryParameters(key, value, isVbuFwEnabled)) || [];
    }
  }
  return [];
};

export const hasAnySearch = (filters: SearchFilters): boolean => {
  return Boolean(
    filters && (
      filters?.any || filters?.mdt || filters?.lmu || filters?.jpodConfig ||
      filters?.jpodFirmware || filters?.ble3Firmware || filters?.vbu3Firmware ||
      filters?.androidMdtFirmware || filters?.vbusDatabase || filters?.radioFirmware ||
      filters?.sentinelAssociation || filters?.idReportTimestamp === NO_ID_REPORT
    ));
};

export const isNestedSort = (sort): boolean => (nestedFields.some(field => isEqual(field, sort)));

export const hasMaxSearch = (filters: SearchFilters): boolean => {
  return Boolean(filters && (filters?.esn?.split(' OR ')?.length > MAX_IDENTIFIER_SEARCH_LIMIT || filters?.serialNumber?.split(' OR ')?.length > MAX_IDENTIFIER_SEARCH_LIMIT));
};

export const getAnySearchCriteria = (
  search: SearchFilters,
  viewSubGroups: boolean,
  includeCurrentSort: boolean = true): APIGatewaySmartSearch => {
  const anySearch = {...search};
  if (viewSubGroups) {
    anySearch.hierLevel = 'ALL';
  }
  if (!includeCurrentSort && anySearch?.query?.sort) {
    anySearch.query.sort = [{
      id : {
        order : 'asc'
      }
    }];
  }
  return {
    smartSearch : {
      search : {
        ...anySearch,
        hierLevel : viewSubGroups ? 'ALL' : ''
      }
    }
  };
};

export const getNewSearchCriteria = ({
  search,
  account,
  viewSubGroups,
  sort,
  excludedIds,
  excluded,
  includeDefaultSort = false,
  isVbuFwEnabled = true
}) => {
  const sortOrder        = [sort?.includes('-')];
  const sortOn           = getSortOn(sort, sortOrder, isVbuFwEnabled);
  const sortParam        = sort?.replace('-', '');
  const defaultSortParam = getDefaultSortParam(sortParam);
  const hasExtensions    = hasDefaultExtensionsSortParam(sortParam);

  if (includeDefaultSort) {
    const defaultSort = {
      id : {
        order : 'asc'
      }
    };
    sortOn.push(defaultSort);
  } else {
    const defaultSort = {
      [defaultSortParam] : {
        order : sortOrder[0] ? 'desc' : 'asc',
        ...(hasExtensions && {missing: '_missing'})
      }
    };
    sortOn.push(defaultSort);
  }

  const mustQuery = {must: getQueries(search, isVbuFwEnabled)};

  if (excludedIds?.length > 0 && excluded) {
    mustQuery.must_not = [
      {
        terms : {
          id : excludedIds
        }
      }
    ];
  }

  return {
    sortOn : {
      type  : 'device',
      query : {
        query : {
          bool : mustQuery
        },
        sort : sortOn
      },
      accountId : account,
      hierLevel : viewSubGroups ? 'ALL' : ''
    },
    includeSortParams : true,
    includeParams     : true
  };
};

const searchWildCard = (key, value) => (
  {wildcard: {[key]: value}}
);

const searchMatch = (key, value) => (
  {match: {[key]: value}}
);

const searchTerms = (key, value) => (
  {terms: {[key]: value?.split(' OR '), boost: 1.0}}
);

const getQueries = (search, isVbuFwEnabled = true) => {
  if (!isNil(search)) {
    const items = Object.entries(search).filter(
      ([, value]) => !isNil(value)
    );
    return items.map(([key, value]) => {
      if (key === 'esn' || key === 'serialNumber') {
        return searchTerms(key, value);
      } else if (key === 'airId' || key === 'architecture' || key === 'category' || key === 'typeId') {
        return searchMatch(key, value);
      } else if (key === 'name' || key === 'iccid' || key === 'imei' || key === 'imei2' || key === 'meid' || key === 'userESN') {
        return searchWildCard(key, value);
      } else {
        return searchQueryParameters(key, value, isVbuFwEnabled);
      }
    });
  }
  return [];
};

export const getExcludeDevicesQuery = ({
  search,
  excludedIds,
  accountId,
  viewSubGroups,
  isVbuFwEnabled = true
}) => {
  if (isNil(search)) {
    const criteria = {
      type  : 'device',
      query : {
        query : {
          bool : {
            must_not : [
              {
                terms : {
                  id : excludedIds
                }
              }
            ]
          }
        }
      },
      accountId,
      hierLevel : viewSubGroups ? 'ALL' : ''
    };
    return {
      smartSearch : {
        search : criteria
      }
    };
  } else {
    const items = Object.entries(search).filter(
      ([, value]) => !isNil(value)
    );
    const criteria = {
      type  : 'device',
      query : {
        query : {
          bool : {
            must_not : [
              {
                terms : {
                  id : excludedIds
                }
              }
            ],
            must : items.map(item => getIdentifiers(item, isVbuFwEnabled)) || []
          }
        }
      },
      accountId,
      hierLevel : viewSubGroups ? 'ALL' : ''
    };
    return {
      smartSearch : {
        search : criteria
      }
    };
  };
};
